/*
==========================================
STAMP IMAGE PROCESSOR
版本: v3.1.0
文件: assets/stamp-image-processor.js
说明: 照片处理算法，支持4种风格 + remove.bg 背景去除

支持的风格:
1. line-art   - 基础线稿（清晰轮廓 + 头发填充）
2. enhanced   - 增强线稿（更锐利的边缘）
3. sketch     - 素描风格（保留阴影）
4. silhouette - 剪影（纯黑色轮廓）

新增:
- removeBackground(): 调用 remove.bg API 去除背景，只保留人物/动物
  使用前需设置 window.StampImageProcessor.REMOVEBG_API_KEY = '你的Key'
  若 Key 未填 / API 失败，静默降级返回原图

使用方法:
const processedImage = await window.StampImageProcessor.convertToLineArt(img);
==========================================
*/

window.StampImageProcessor = {

  // API Key 由 stamp-customizer.js 在 init() 中注入
  REMOVEBG_API_KEY: '',

  // ==================== 背景去除（remove.bg API）====================
  // imageDataUrl: 裁剪后的 dataURL
  // 返回: 透明背景 PNG 的 dataURL（失败时返回原 dataURL）
  removeBackground: async function(imageDataUrl) {
    const key = this.REMOVEBG_API_KEY;

    // Key 未配置时静默跳过
    if (!key || key === 'YOUR_REMOVEBG_API_KEY') {
      console.warn('[StampImageProcessor] remove.bg Key 未配置，跳过抠图');
      return imageDataUrl;
    }

    try {
      // dataURL → Blob
      const blob = await fetch(imageDataUrl).then(r => r.blob());

      const fd = new FormData();
      fd.append('image_file', blob, 'photo.png');
      fd.append('size', 'auto');    // 自动分辨率
      fd.append('channels', 'rgba'); // 输出透明 PNG

      const res = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: { 'X-Api-Key': key },
        body: fd
      });

      if (!res.ok) {
        const errMsg = await res.text().catch(() => String(res.status));
        console.warn('[StampImageProcessor] remove.bg 调用失败，降级:', errMsg);
        return imageDataUrl; // 降级：返回原图
      }

      // 成功：返回透明 PNG dataURL
      const resultBlob = await res.blob();
      return await this._blobToDataURL(resultBlob);

    } catch (err) {
      console.warn('[StampImageProcessor] remove.bg 异常，降级:', err);
      return imageDataUrl; // 降级：返回原图
    }
  },

  // Blob → dataURL
  _blobToDataURL: function(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  // ==================== 通用 Line Art 核心（供4档风格共用）====================
  // options 参数说明：
  //   windowSize  - 自适应阈值窗口（小=细节多，大=线条简洁）
  //   threshVal   - 阈值敏感度（小=检测更多线条，大=只保留主轮廓）
  //   offsetFactor- 偏移系数（大=线条更粗）
  //   hairThresh  - 深色填充阈值（大=填充更多深色区域）
  //   denoiseKeep - 降噪保留下限（像素黑邻居数 > 此值才保留）
  //   denoiseFill - 降噪填充上限（像素黑邻居数 >= 此值强制填黑）
  //   smoothPasses- 边缘平滑次数（0=不平滑，1=标准，2=更光滑）
  _processLineArt: function(img, options) {
    return new Promise((resolve, reject) => {
      try {
        const {
          windowSize   = 11,
          threshVal    = 155,
          offsetFactor = 0.15,
          hairThresh   = 90,
          denoiseKeep  = 2,
          denoiseFill  = 7,
          smoothPasses = 1,
          maxSize      = 1800,
          contrast     = '100%',
          blur         = '0.5px'
        } = options;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let width = img.width, height = img.height;
        if (width > height) { if (width > maxSize) { height = Math.round(height*(maxSize/width)); width = maxSize; } }
        else { if (height > maxSize) { width = Math.round(width*(maxSize/height)); height = maxSize; } }
        canvas.width = width; canvas.height = height;

        // 预处理：白底 + 对比度 + 轻微模糊
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width; tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, width, height);
        tempCtx.drawImage(img, 0, 0, width, height);
        tempCtx.filter = `contrast(${contrast}) blur(${blur})`;
        tempCtx.drawImage(tempCanvas, 0, 0);
        tempCtx.filter = 'none';

        let imgData = tempCtx.getImageData(0, 0, width, height);
        let data = new Uint8ClampedArray(imgData.data);

        // 转灰度
        for (let i = 0; i < data.length; i += 4) {
          const gray = data[i]*0.299 + data[i+1]*0.587 + data[i+2]*0.114;
          data[i] = data[i+1] = data[i+2] = gray;
        }

        // 自适应阈值层（轮廓线）
        const sketchLayer = new Uint8ClampedArray(data.length);
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            let sum = 0, count = 0;
            for (let dy = -windowSize; dy <= windowSize; dy++) {
              for (let dx = -windowSize; dx <= windowSize; dx++) {
                const ny = y+dy, nx = x+dx;
                if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
                  sum += data[(ny*width+nx)*4]; count++;
                }
              }
            }
            const localMean = sum / count;
            const offset = (255 - threshVal) * offsetFactor;
            const val = data[idx] < (localMean - offset) ? 0 : 255;
            sketchLayer[idx] = sketchLayer[idx+1] = sketchLayer[idx+2] = val;
            sketchLayer[idx+3] = 255;
          }
        }

        // 深色填充层（头发/深色区域）
        const hairLayer = new Uint8ClampedArray(data.length);
        hairLayer.fill(255);
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] < hairThresh) hairLayer[i] = hairLayer[i+1] = hairLayer[i+2] = 0;
          hairLayer[i+3] = 255;
        }
        // 形态学膨胀填充边缘
        const tempHair = new Uint8ClampedArray(hairLayer);
        for (let y = 1; y < height-1; y++) {
          for (let x = 1; x < width-1; x++) {
            const idx = (y*width+x)*4;
            if (tempHair[idx] === 255) {
              let hasBlack = false;
              for (let dy = -1; dy <= 1 && !hasBlack; dy++)
                for (let dx = -1; dx <= 1 && !hasBlack; dx++)
                  if (tempHair[((y+dy)*width+(x+dx))*4] === 0) hasBlack = true;
              if (hasBlack) hairLayer[idx] = hairLayer[idx+1] = hairLayer[idx+2] = 0;
            }
          }
        }

        // 合并两层
        let result = new Uint8ClampedArray(data.length);
        for (let i = 0; i < data.length; i += 4) {
          const val = (sketchLayer[i] === 0 || hairLayer[i] === 0) ? 0 : 255;
          result[i] = result[i+1] = result[i+2] = val;
          result[i+3] = 255;
        }

        // 降噪（2轮）
        for (let iter = 0; iter < 2; iter++) {
          const temp = new Uint8ClampedArray(result);
          for (let y = 1; y < height-1; y++) {
            for (let x = 1; x < width-1; x++) {
              const idx = (y*width+x)*4;
              let bc = 0;
              for (let dy = -1; dy <= 1; dy++)
                for (let dx = -1; dx <= 1; dx++)
                  if (temp[((y+dy)*width+(x+dx))*4] === 0) bc++;
              if (bc <= denoiseKeep)  result[idx] = result[idx+1] = result[idx+2] = 255;
              else if (bc >= denoiseFill) result[idx] = result[idx+1] = result[idx+2] = 0;
            }
          }
        }

        // 边缘平滑（形态学闭运算，可多次）
        for (let pass = 0; pass < smoothPasses; pass++) {
          const smoothed = new Uint8ClampedArray(result);
          // 膨胀
          for (let y = 1; y < height-1; y++) {
            for (let x = 1; x < width-1; x++) {
              const idx = (y*width+x)*4;
              let hasBlack = false;
              for (let dy = -1; dy <= 1 && !hasBlack; dy++)
                for (let dx = -1; dx <= 1 && !hasBlack; dx++)
                  if (result[((y+dy)*width+(x+dx))*4] === 0) hasBlack = true;
              if (hasBlack) smoothed[idx] = smoothed[idx+1] = smoothed[idx+2] = 0;
            }
          }
          // 腐蚀
          const final2 = new Uint8ClampedArray(smoothed);
          for (let y = 1; y < height-1; y++) {
            for (let x = 1; x < width-1; x++) {
              const idx = (y*width+x)*4;
              let allBlack = true;
              for (let dy = -1; dy <= 1 && allBlack; dy++)
                for (let dx = -1; dx <= 1 && allBlack; dx++)
                  if (smoothed[((y+dy)*width+(x+dx))*4] === 255) allBlack = false;
              if (!allBlack) final2[idx] = final2[idx+1] = final2[idx+2] = 255;
            }
          }
          result = final2;
        }

        imgData.data.set(result);
        ctx.putImageData(imgData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch(err) { reject(err); }
    });
  },

  // ==================== Style 1: Classic Line Art（经典，均衡细节）====================
  // windowSize=11, threshVal=155, offset=0.15, hairThresh=90
  // 效果：标准印章线稿，轮廓清晰，细节适中
  convertToLineArt: async function(img) {
    return this._processLineArt(img, {
      windowSize: 11, threshVal: 155, offsetFactor: 0.15,
      hairThresh: 90, denoiseKeep: 2, denoiseFill: 7, smoothPasses: 1
    });
  },

  // ==================== Style 2: Detailed Line Art（精细，最多细节）====================
  // windowSize=6（小窗口捕捉更多局部变化）, threshVal=140（更敏感）, offset=0.20
  // hairThresh=105（抓更多深色纹理）, 降噪保守（保留细线）
  // 效果：线条最密集，纹理和毛发细节丰富，适合复杂图案
  convertToEnhancedLineArt: async function(img) {
    return this._processLineArt(img, {
      windowSize: 6, threshVal: 140, offsetFactor: 0.20,
      hairThresh: 105, denoiseKeep: 1, denoiseFill: 8, smoothPasses: 0,
      contrast: '120%', blur: '0.3px'
    });
  },

  // ==================== Style 3: Clean Line Art（简洁，干净轮廓）====================
  // windowSize=17（大窗口只看整体明暗差异）, threshVal=170（宽松）, offset=0.10
  // hairThresh=70（只填最深的区域）, 降噪积极（去掉杂散点）, 平滑2次
  // 效果：线条最少最干净，适合简单人像或想要极简效果的印章
  convertToSketch: async function(img) {
    return this._processLineArt(img, {
      windowSize: 17, threshVal: 170, offsetFactor: 0.10,
      hairThresh: 70, denoiseKeep: 3, denoiseFill: 6, smoothPasses: 2,
      contrast: '90%', blur: '0.8px'
    });
  },

  // ==================== Style 4: Bold Line Art（粗犷，强对比）====================
  // windowSize=11, threshVal=155, offset=0.28（更大偏移=更粗线条）
  // hairThresh=115（深色区域全部填实）, 降噪适中, 平滑1次
  // 效果：线条最粗最重，黑色填充区域大，印章效果最强烈
  convertToSilhouette: async function(img) {
    return this._processLineArt(img, {
      windowSize: 11, threshVal: 148, offsetFactor: 0.28,
      hairThresh: 115, denoiseKeep: 2, denoiseFill: 6, smoothPasses: 1,
      contrast: '130%', blur: '0.5px'
    });
  },

    // ==================== 辅助函数 ====================
  _applyGaussianBlur: function(data, width, height, radius) {
    const output = new Uint8ClampedArray(data.length);
    const kernel = this._createGaussianKernel(radius);
    const kernelSize = kernel.length;
    const halfKernel = Math.floor(kernelSize / 2);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0, weightSum = 0;
        for (let ky = 0; ky < kernelSize; ky++) {
          for (let kx = 0; kx < kernelSize; kx++) {
            const py = y + ky - halfKernel, px = x + kx - halfKernel;
            if (py >= 0 && py < height && px >= 0 && px < width) {
              const idx = (py * width + px) * 4;
              const weight = kernel[ky][kx];
              sum += data[idx] * weight;
              weightSum += weight;
            }
          }
        }
        const idx = (y * width + x) * 4;
        const value = sum / weightSum;
        output[idx] = output[idx+1] = output[idx+2] = value;
        output[idx+3] = 255;
      }
    }
    return output;
  },
  
  _createGaussianKernel: function(radius) {
    const size = radius * 2 + 1;
    const kernel = [];
    const sigma = radius / 2;
    let sum = 0;
    for (let y = 0; y < size; y++) {
      kernel[y] = [];
      for (let x = 0; x < size; x++) {
        const dx = x - radius, dy = y - radius;
        const value = Math.exp(-(dx*dx + dy*dy) / (2 * sigma * sigma));
        kernel[y][x] = value;
        sum += value;
      }
    }
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++)
        kernel[y][x] /= sum;
    return kernel;
  },
  
  _applyCannyEdgeDetection: function(data, width, height) {
    const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
    const output = new Uint8ClampedArray(data.length);
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0, gy = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y+ky)*width+(x+kx))*4;
            const ki = (ky+1)*3+(kx+1);
            const pixel = data[idx];
            gx += pixel * sobelX[ki];
            gy += pixel * sobelY[ki];
          }
        }
        const magnitude = Math.sqrt(gx*gx + gy*gy);
        const idx = (y*width+x)*4;
        const value = magnitude > 50 ? magnitude : 0;
        output[idx] = output[idx+1] = output[idx+2] = value;
        output[idx+3] = 255;
      }
    }
    return output;
  },
  
  _applyDilation: function(data, width, height, iterations) {
    let current = new Uint8ClampedArray(data);
    for (let iter = 0; iter < iterations; iter++) {
      const output = new Uint8ClampedArray(current.length);
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          let maxValue = 0;
          for (let dy = -1; dy <= 1; dy++)
            for (let dx = -1; dx <= 1; dx++)
              maxValue = Math.max(maxValue, current[((y+dy)*width+(x+dx))*4]);
          const idx = (y*width+x)*4;
          output[idx] = output[idx+1] = output[idx+2] = maxValue;
          output[idx+3] = 255;
        }
      }
      current = output;
    }
    return current;
  },
  
  _applyErosion: function(data, width, height, iterations) {
    let current = new Uint8ClampedArray(data);
    for (let iter = 0; iter < iterations; iter++) {
      const output = new Uint8ClampedArray(current.length);
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          let minValue = 255;
          for (let dy = -1; dy <= 1; dy++)
            for (let dx = -1; dx <= 1; dx++)
              minValue = Math.min(minValue, current[((y+dy)*width+(x+dx))*4]);
          const idx = (y*width+x)*4;
          output[idx] = output[idx+1] = output[idx+2] = minValue;
          output[idx+3] = 255;
        }
      }
      current = output;
    }
    return current;
  },
  
  _applyMorphologicalFill: function(data, width, height) {
    const result = new Uint8ClampedArray(data);
    const dilated = this._applyDilation(result, width, height, 3);
    return this._applyErosion(dilated, width, height, 3);
  },
  
  _applyMorphologicalSmooth: function(data, width, height) {
    const eroded = this._applyErosion(data, width, height, 2);
    return this._applyDilation(eroded, width, height, 2);
  }

};

if (typeof module !== 'undefined' && module.exports) {
  module.exports = window.StampImageProcessor;
}